// app.quotes.$id.tsx — the single-quote detail/edit screen (line editing,
// send/resend, accept, counter-offers, AI price suggestions, PDF/email
// actions). Highest-traffic merchant-facing screen in the app.
export const quoteDetail = {
  trialGate: {
    // Server-side action error when a plan-gated intent (send/resend/accept/
    // counter/suggest) is attempted after the trial has ended. `{{suffix}}` is
    // pre-built in the route (" on 2026-01-01" or "") — kept out of the
    // dictionary since it's a date, not language-dependent text.
    actionError: "Your trial ended{{suffix}} — choose a plan to send, accept, or re-price quotes.",
    banner: "Your trial ended{{suffix}}. Choose a plan to send, accept, or re-price quotes — viewing stays available.",
    choosePlanLink: "Choose a plan",
  },
  notices: {
    counterDispatched: "AI counter-offer dispatched. Suggestions appear here when ready (usually ~10 seconds).",
    counterFailed: "Couldn't reach the AI counter-offer service. Try again, or contact support if it persists.",
    suggesting: "AI is pricing your lines from this company's history. Refresh in a few seconds to see suggestions.",
    suggestFailed: "Couldn't start the AI pricing job. Try again, or contact support if it persists.",
    pricesApplied: "Applied the AI-suggested prices to this quote.",
  },
  errors: {
    sendEmailFailed: "Couldn't send the quote email. Please try again or contact support.",
    rotateTokenWrongStatus: "Only SENT/VIEWED/ACCEPTED/DECLINED/EXPIRED quotes have a buyer link to rotate.",
    resendCooldown: "Wait {{wait}}s before resending to the same buyer.",
    resendEmailFailed: "Couldn't re-send the email. Please try again or contact support.",
    notAcceptableState: "Quote is not in a state that can be accepted.",
    expiredCannotAccept: "This quote has expired — duplicate it to a fresh quote to accept at current pricing.",
    alreadyResponded: "Quote already responded to.",
    draftOrderCreateFailed: "Couldn't create draft order. Please try again.",
    missingCounterOfferId: "Missing counterOfferId",
    counterOfferAlreadyApplied: "Counter-offer already applied or not found.",
    suggestPricesNotDraft: "AI price suggestions are available on draft quotes.",
    missingSuggestionId: "Missing suggestionId",
    applyPricesNotDraft: "Prices can only be applied to a draft quote.",
    suggestionsAlreadyResolved: "These suggestions were already applied or dismissed.",
  },
  companyReadiness: {
    defaultCompanyName: "This company",
    bothMissing:
      "Almost there — {{who}} just needs a contact and a location in Shopify Admin before buyers can accept this quote. Add them under Companies, and this quote is good to go.",
    missingContactOnly:
      "Almost there — {{who}} just needs a contact in Shopify Admin before buyers can accept this quote. Add it under Companies, and this quote is good to go.",
    missingLocationOnly:
      "Almost there — {{who}} just needs a location in Shopify Admin before buyers can accept this quote. Add it under Companies, and this quote is good to go.",
    openCompaniesLink: "Open Companies",
  },
  shopifyRejectedBanner: "Shopify rejected the draft order: {{messages}}",
  sendResultBanner: {
    sentDelivered: "Quote sent — the buyer email is on its way.",
    resentDelivered: "Email re-sent to the buyer.",
    sentNotDelivered:
      "Quote marked as sent, but the buyer email didn't go out. Copy the buyer link above to share it, or use “Re-send email” once email is configured.",
    resentNotDelivered:
      "Email queued but delivery wasn't confirmed (check Resend config). Use “Re-send email” to retry.",
  },
  rotatedBanner: {
    prefix: "New buyer link:",
    suffix: "The previous link no longer works — re-send the email to deliver the new link to the buyer.",
    copyNewLinkButton: "Copy new link",
  },
  status: {
    heading: "Status",
    noCompanyName: "(no company name)",
  },
  buyer: {
    heading: "Buyer",
    linkLabel: "Buyer link: {{url}}",
    shareLinkHint:
      "Anyone with this link can view and respond to the quote — share it directly if the email doesn't arrive.",
    viewedOnce: "Viewed {{count}} time",
    viewedMany: "Viewed {{count}} times",
    lastViewedSuffix: ", last on {{date}}",
    notOpenedYet: "Buyer hasn't opened this quote yet.",
    copyButtonDefaultLabel: "Copy buyer link",
    copiedLabel: "Copied ✓",
    copyPromptMessage: "Copy the buyer link:",
  },
  lines: {
    heading: "Lines",
    qty: "qty {{qty}}",
    eachSuffix: "each",
    lineSubtotalPrefix: "line:",
  },
  aiSuggestions: {
    heading: "AI price suggestions",
    notProDescription:
      "Let AI price these lines from this company's own order history — every suggestion stays inside your pricing rules. A Pro feature.",
    upgradeToProLink: "Upgrade to Pro",
    refusedFallback:
      "Not enough order history yet to suggest prices confidently. Add a few orders for this company and try again.",
    dismissButton: "Dismiss",
    modelCostLine: "{{model}} · ${{cost}}",
    withinLimitsBadge: "Within your limits",
    keptNoChange: "Kept — no change suggested",
    applyPricesButton: "Apply prices",
    pricingInProgress: "Pricing your lines… this usually takes a few seconds.",
    refreshLink: "Refresh",
    defaultCompanyName: "this company",
    promptDescription:
      "Let AI price these lines from {{company}}'s order history — every suggestion stays inside your pricing rules.",
    suggestPricesButton: "Suggest prices with AI",
  },
  dataSufficiencyBadge: "Data sufficiency: {{level}}",
  actions: {
    heading: "Actions",
    sendButton: "Send to buyer",
    markAcceptedButton: "Mark accepted (and create draft order)",
    resendEmailButton: "Re-send email to buyer",
    rotateLinkButton: "Rotate buyer link (revoke + reissue)",
    draftCounterButton: "Draft AI counter-offer",
    unlockOnGrowthBadge: "Unlock on Growth",
    cancelButton: "Cancel quote",
    duplicateButton: "Duplicate as new draft",
    downloadPdfButton: "Download PDF",
    draftOrderLabel: "Draft order: {{name}}",
  },
  counterOffer: {
    heading: "AI counter-offer",
    scanSummary: "{{count}} orders scanned · {{model}} · ${{cost}}",
    appliedBadge: "Applied {{date}}",
    applyButton: "Apply as new quote",
  },
  activity: {
    heading: "Activity ({{count}})",
    actor: {
      buyer: "buyer",
      system: "system",
      merchant: "merchant",
    },
  },
};
